import { orderQueue, type QueuedStudent } from './queue';

export type SessionMode = 'private' | 'halaqa';

export type RoomState = {
  mode: SessionMode;
  muqriId: string;
  participants: string[];
  currentReciterId: string | null;
};

/*
 * `canPublish(state, userId)` used to live here, and it is gone rather than
 * wired up. It was a second copy of the rule in functions/sessionToken.js's
 * authorise(), nothing imported it, and four tests guarded it — so deleting
 * its participant check left the whole suite green while the real rule, the
 * one that actually decides whether a microphone opens, went on being tested
 * only on the server.
 *
 * There is nowhere honest to wire it. The publish right reaches the client as
 * `canPublish` on getSessionToken's response — decided server-side, from the
 * stored session, and baked into the LiveKit grant — and CallScreen renders
 * from that. A local recomputation could only ever agree with the server or
 * be wrong; agreeing adds nothing, and disagreeing shows the student the
 * opposite of what their microphone is doing.
 */

/**
 * Round the circle, in queue order: the student after the current reciter,
 * wrapping to the first when the circle runs out — a حلقة passes the turn
 * on, it doesn't keep re-picking "whoever has waited longest" (that starves
 * everyone past the second student, since the student just heard from goes
 * straight back to being the longest-waiting one).
 *
 * The anchor is the current reciter's position in the full join-time order,
 * not their index among present students — so if they have since left the
 * room, the turn continues from where they were instead of restarting the
 * circle at the first student. Someone still queued but no longer present
 * (or the معلم) is never a candidate.
 */
export function nextReciter(state: RoomState, queue: QueuedStudent[]): string | null {
  const ordered = orderQueue(queue);
  const isEligible = (s: QueuedStudent) =>
    s.studentId !== state.muqriId && state.participants.includes(s.studentId);
  const present = ordered.filter(isEligible);
  if (present.length === 0) return null;

  if (state.currentReciterId === null) return present[0].studentId;

  const anchorIndex = ordered.findIndex((s) => s.studentId === state.currentReciterId);
  if (anchorIndex === -1) return present[0].studentId;

  const afterAnchor = ordered.slice(anchorIndex + 1).find(isEligible);
  return afterAnchor ? afterAnchor.studentId : present[0].studentId;
}
